import React, { useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Control, useFieldArray } from "react-hook-form";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import styled from "@emotion/styled";
import { v4 as uuidV4 } from "uuid";

import Button from "components/ui/button/Button";
import { createUseDebounce } from "lib/debounce";
import {
  BidQuestionGroupDto,
  BidQuestionObj,
} from "lib/hooks/api/bid/bid_question/useBidQuestions";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { BidReportType, inferEnumType } from "lib/types/graphQLEnums";
import { Flex } from "../../../styles/utility-components";
import { AddOrEditCriteriaDrawer } from "./AddOrEditCriteriaDrawer";
import BidQuestionItem from "./BidQuestionItem";
import { DeleteCriteriaModal } from "./DeleteCriteriaModal";

export type BidSelfServeForm = {
  questionGroups: BidQuestionGroupDto[];
};

type Props = {
  groupName: string;
  groupIndex: number;
  control: Control<BidSelfServeForm>;
  submitForm: () => void;
};

export function BidQuestionGroup({ groupName, groupIndex, control, submitForm }: Props) {
  // Used to scroll to the bottom of the question list
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: currentUser } = useCurrentUser();
  const {
    fields: questions,
    append,
    remove,
    update,
    move,
  } = useFieldArray({
    control,
    // Using groupIndex to get the correct group by path
    name: `questionGroups.${groupIndex}.questions`,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Debounce the dragging submission to prevent too many API calls
  const useDebouncedSubmit = createUseDebounce(1000); // 1 second debounce
  const debouncedSubmit = useDebouncedSubmit(submitForm);
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over?.id || active.id === over.id) return;

    const activeIndex = questions.findIndex((q) => q.id === active.id);
    const overIndex = questions.findIndex((q) => q.id === over.id);

    if (activeIndex === -1 || overIndex === -1) return;

    flushSync(() => move(activeIndex, overIndex));

    debouncedSubmit();
  };

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [currentQuestion, setCurrentQuestion] = useState<BidQuestionObj | null>(null);

  return (
    <>
      <Container ref={containerRef}>
        <AddCriteriaBtnContainer>
          <Button
            icon="plus"
            buttonType="secondary"
            fullWidth={false}
            onClick={() => {
              setCurrentQuestion(null);
              setDrawerOpen(true);
            }}
          >
            Question
          </Button>
        </AddCriteriaBtnContainer>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext
            items={questions.map((q) => q.id)}
            strategy={verticalListSortingStrategy}
          >
            {questions.map((q) => (
              <BidQuestionItem
                question={q}
                key={q.id}
                openEditDrawer={() => {
                  setCurrentQuestion(q);
                  setDrawerOpen(true);
                }}
                openDeleteModal={() => {
                  setCurrentQuestion(q);
                  setOpenDeleteModal(true);
                }}
              />
            ))}
          </SortableContext>
        </DndContext>
        {/* Used to scroll to the bottom of the question list */}
        <span style={{ visibility: "hidden" }} />
      </Container>
      <AddOrEditCriteriaDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        initialTitle={currentQuestion?.title}
        initialQuestion={currentQuestion?.question}
        onSave={(title, question) => {
          if (currentQuestion != null) {
            const questionIndex = questions.findIndex((q) => q.id === currentQuestion.id);
            update(questionIndex, {
              ...currentQuestion,
              id: currentQuestion.id,
              title,
              question,
            });
          } else {
            const maxOrder = Math.max(...questions.map((q) => q.order ?? 0));

            append({
              id: uuidV4(), // fake ID for now - good for keys in UI
              title,
              question,
              order: maxOrder + 1,
              groupName,
              teamId: currentUser?.team.id,
              companyId: currentUser?.company.guid,
              autoQualify: true,
              // This form only manages RequirementSummary questions (BidSelfServe
              // fetches with that reportType). New rows inherit the same type so
              // they upsert without forcing the user to pick one.
              reportType: inferEnumType<BidQuestionObj["reportType"]>(
                BidReportType.RequirementSummary,
              ),
            });
          }

          // Honestly the most reliable way to scroll to the question
          // just edited or deleted. Will usually be at the bottom of
          // the list and other methods I've tried are super unreliable
          containerRef.current?.lastElementChild?.scrollIntoView({ behavior: "smooth" });

          setDrawerOpen(false);
          // Need to explicitly call submitForm. Changing the type of the
          //  button to "submit" doesn't work because a drawer is outside of
          //  the DOM of the form
          submitForm();
        }}
      />
      {currentQuestion && (
        <DeleteCriteriaModal
          open={openDeleteModal}
          onOpenChange={(open) => setOpenDeleteModal(open)}
          deleteQuestion={() => {
            const questionIndex = questions.findIndex((q) => q.id === currentQuestion.id);
            remove(questionIndex);
            submitForm();
          }}
          criteriaTitle={currentQuestion.title}
        />
      )}
    </>
  );
}

const Container = styled.div({
  padding: "0px 24px 24px",
});

const AddCriteriaBtnContainer = styled(Flex)({
  justifyContent: "flex-end",
  padding: 16,
  paddingTop: 8,
  paddingRight: 0,
});
